import { useState } from "react";
import { motion } from "framer-motion";

const LAYERS = [
  {
    idx: "L6",
    name: "AI Agents",
    color: "#CCFF00",
    desc: "Autonomous research, outreach, and qualification agents that operate 24/7 on top of every lower layer.",
    surface: "Agent Runtime",
    specs: ["7 agents deployed", "Self-improving prompts", "Human-in-the-loop"],
  },
  {
    idx: "L5",
    name: "Operations",
    color: "#FFFFFF",
    desc: "Routing, dedupe, and CRM sync. The layer that keeps reps, marketing, and RevOps in a single source of truth.",
    surface: "Ops Backbone",
    specs: ["Salesforce / HubSpot", "212ms round-trip", "Audit-logged"],
  },
  {
    idx: "L4",
    name: "Activation",
    color: "#D946EF",
    desc: "Multi-channel orchestration — inbox, LinkedIn, ads, and warm intros — sequenced against signal strength.",
    surface: "Orchestration",
    specs: ["Signal-weighted", "Throttle-aware", "Deliverability tuned"],
  },
  {
    idx: "L3",
    name: "Signal System",
    color: "#FFB800",
    desc: "Real-time buying intent: job changes, funding, tech installs, review activity, posting velocity.",
    surface: "Signal Capture",
    specs: ["14 sources", "<5min latency", "Dedup + score"],
  },
  {
    idx: "L2",
    name: "TAM & Intelligence",
    color: "#00F0FF",
    desc: "Your total addressable market, mapped, enriched, and scored by fit and likelihood-to-close.",
    surface: "Intelligence",
    specs: ["98.2% enriched", "ICP-scored", "Waterfall validated"],
  },
  {
    idx: "L1",
    name: "Data Foundation",
    color: "#007AFF",
    desc: "Clean, normalized account structure — the concrete slab the entire engine is built on.",
    surface: "Foundation",
    specs: ["Source-ranked", "Versioned", "Observable"],
  },
];

export default function RevenueEngine() {
  const [active, setActive] = useState(0); // top-most by default (AI Agents)

  return (
    <section
      id="engine"
      data-testid="revenue-engine-section"
      className="relative bg-[#050505] border-t border-white/10 py-28 md:py-40"
    >
      <div className="absolute inset-0 precision-grid-dense opacity-20 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <SectionHeader
          overline="Architecture / 03"
          title={
            <>
              The 6-Layer
              <br />
              <span className="text-white">Revenue Engine.</span>
            </>
          }
          sub="Not six services bolted together. One machine, six interoperating layers — each one observable, each one owned by your team after handover."
        />

        <div className="mt-16 grid grid-cols-12 gap-6 lg:gap-10">
          {/* Interactive stack */}
          <div className="col-span-12 lg:col-span-7 space-y-2" data-testid="engine-stack">
            {LAYERS.map((layer, i) => {
              const isActive = active === i;
              return (
                <button
                  key={layer.name}
                  onClick={() => setActive(i)}
                  onMouseEnter={() => setActive(i)}
                  data-testid={`engine-layer-${layer.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`group w-full text-left relative border transition-all duration-300 ${
                    isActive ? "border-white/25 bg-white/[0.03]" : "border-white/10 hover:border-white/20"
                  }`}
                  style={{
                    boxShadow: isActive ? `inset 6px 0 0 ${layer.color}` : "none",
                  }}
                >
                  <div className="flex items-center p-5 md:p-6 gap-6">
                    <span
                      className="font-mono text-[11px] tracking-[0.3em]"
                      style={{ color: isActive ? layer.color : "#52525B" }}
                    >
                      {layer.idx}
                    </span>
                    <div className="flex-1 flex items-center justify-between gap-4 flex-wrap">
                      <h3
                        className={`font-display font-bold uppercase tracking-tighter text-2xl md:text-3xl transition-colors ${
                          isActive ? "text-white" : "text-zinc-500 group-hover:text-zinc-300"
                        }`}
                      >
                        {layer.name}
                      </h3>
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hidden md:inline">
                        {layer.surface}
                      </span>
                    </div>
                    <span
                      className="h-2 w-2 rounded-full transition-all"
                      style={{
                        background: isActive ? layer.color : "rgba(255,255,255,0.15)",
                        boxShadow: isActive ? `0 0 14px ${layer.color}` : "none",
                      }}
                    />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Detail panel */}
          <div className="col-span-12 lg:col-span-5">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="glass p-7 md:p-8 relative"
              data-testid="engine-detail"
              style={{
                boxShadow: `inset 0 1px 0 0 ${LAYERS[active].color}22`,
              }}
            >
              <div className="flex items-center justify-between mb-5">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  Layer / {LAYERS[active].idx}
                </span>
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{
                    background: LAYERS[active].color,
                    boxShadow: `0 0 10px ${LAYERS[active].color}`,
                  }}
                />
              </div>

              <h4
                className="font-display font-bold text-3xl md:text-4xl uppercase tracking-tighter leading-none"
                style={{ color: LAYERS[active].color }}
              >
                {LAYERS[active].name}
              </h4>

              <p className="mt-5 font-body text-zinc-300 text-[15px] leading-relaxed">
                {LAYERS[active].desc}
              </p>

              <div className="mt-6 pt-5 border-t border-dashed border-white/10 grid grid-cols-1 gap-2.5">
                {LAYERS[active].specs.map((spec) => (
                  <div key={spec} className="flex items-center justify-between font-mono text-[11px] uppercase tracking-wider">
                    <span className="text-zinc-500">spec</span>
                    <span className="text-zinc-200">{spec}</span>
                  </div>
                ))}
              </div>

              {/* Waveform */}
              <div className="mt-6 h-12 flex items-end gap-[2px]">
                {Array.from({ length: 60 }).map((_, i) => {
                  const h = 10 + Math.abs(Math.sin(i * 0.4 + active)) * 38;
                  return (
                    <span
                      key={i}
                      className="flex-1"
                      style={{
                        height: `${h}px`,
                        background: LAYERS[active].color,
                        opacity: 0.25 + Math.sin(i * 0.5 + active) * 0.25 + 0.25,
                      }}
                    />
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function SectionHeader({ overline, title, sub, align = "left" }) {
  return (
    <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      <div className={`flex items-center gap-3 ${align === "center" ? "justify-center" : ""}`}>
        <span className="h-px w-8 bg-[#CCFF00]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">{overline}</span>
      </div>
      <h2 className="mt-5 font-display font-bold uppercase tracking-[-0.04em] leading-[0.9] text-white text-[40px] sm:text-[56px] md:text-[72px]">
        {title}
      </h2>
      {sub ? (
        <p className="mt-5 font-body text-zinc-400 text-[15px] md:text-[17px] leading-relaxed max-w-[56ch]">
          {sub}
        </p>
      ) : null}
    </div>
  );
}
